import { useState } from "react";
import { Navigation, MapPin, Copy, Check, Eye, AlertCircle, Car } from "lucide-react";
import { business } from "../data/business";

const mapsUrl    = `https://maps.google.com/?q=${encodeURIComponent(business.address.full)}`;
const appleUrl   = `https://maps.apple.com/?q=${encodeURIComponent(business.address.full)}`;
const wazeUrl    = `https://www.waze.com/ul?ll=${business.geo.lat},${business.geo.lng}&navigate=yes`;
const embedSrc   = `https://www.google.com/maps?q=${encodeURIComponent(business.address.full)}&output=embed`;

// #75: driving directions cluster. Two anchor origin cities whose drive-time
// estimates are public-fact and already surfaced in About copy — we don't
// invent times, we surface what we've already verified. Apple/Google route
// from origin -> lodge address; Waze opens its own routing with the lodge
// coordinates so the user keeps their current location as the origin.
const lodgeAddr = business.address.full;
const lodgeAddrEnc = encodeURIComponent(lodgeAddr);
type DriveOrigin = { city: string; time: string; origin: string };
const driveOrigins: DriveOrigin[] = [
  { city: "Chattanooga, TN", time: "~1 hr",    origin: "Chattanooga, TN" },
  { city: "Atlanta, GA",     time: "~2.5 hrs", origin: "Atlanta, GA" },
];
function appleFrom(origin: string) {
  return `https://maps.apple.com/?daddr=${lodgeAddrEnc}&saddr=${encodeURIComponent(origin)}`;
}
function googleFrom(origin: string) {
  return `https://www.google.com/maps/dir/${encodeURIComponent(origin)}/${lodgeAddrEnc}`;
}
// Waze can take coords + navigate=yes; it picks up the user's current location
// as the origin automatically. We use the verified lodge coordinates from
// business.geo so the destination pin is exact.
const wazeNavToLodge = `https://www.waze.com/ul?ll=${business.geo.lat},${business.geo.lng}&navigate=yes`;

// New round #64-68: embed already in place; coords now hidden by default
// behind a copy button; nav providers consolidated into a single primary CTA
// + secondary chips; arrival landmark prose surfaced.
export function MapEmbed() {
  const [copied, setCopied] = useState(false);

  const onCopyCoords = async () => {
    const coords = `${business.geo.lat}, ${business.geo.lng}`;
    try {
      await navigator.clipboard?.writeText(coords);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // fall through silently — clipboard not available
    }
  };

  return (
    <section className="map-embed" aria-label="Location map">
      {/* New round #68: arrival landmark prose */}
      <p className="map-landmark">
        <AlertCircle size={16} aria-hidden="true" />
        <span>
          <strong>Look for the green Ocoee Whitewater Lodge sign</strong> on the
          right side of US-64 (Parksville Road), just beyond Parksville Lake.
        </span>
      </p>

      {/* #75: driving directions cluster — anchor cities w/ verified
          drive-time estimates, one card per origin, each with three
          provider buttons. Lives above the iframe so visitors see the
          route options before the heavy map paints. */}
      <div className="map-drive" aria-label="Driving directions from nearby cities">
        <div className="map-drive-head">
          <Car size={16} aria-hidden="true" />
          <span>Driving from nearby</span>
        </div>
        <div className="map-drive-grid">
          {driveOrigins.map((o) => (
            <div key={o.origin} className="map-drive-card">
              <div className="map-drive-city">
                <span className="map-drive-city-name">{o.city}</span>
                <span className="map-drive-city-time">{o.time}</span>
              </div>
              <div className="map-drive-actions">
                <a
                  className="map-drive-btn"
                  href={appleFrom(o.origin)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open Apple Maps directions from ${o.city} (opens in a new tab)`}
                >
                  <MapPin size={12} aria-hidden="true" /> Apple Maps
                </a>
                <a
                  className="map-drive-btn"
                  href={googleFrom(o.origin)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open Google Maps directions from ${o.city} (opens in a new tab)`}
                >
                  <Navigation size={12} aria-hidden="true" /> Google Maps
                </a>
                <a
                  className="map-drive-btn"
                  href={wazeNavToLodge}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open Waze directions to the lodge (opens in a new tab)`}
                >
                  <Car size={12} aria-hidden="true" /> Waze
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="map-embed-frame">
        <iframe
          title={`${business.name} on Google Maps`}
          src={embedSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>

      <div className="map-embed-actions">
        {/* New round #66: one primary directions CTA + secondary chips */}
        <a className="map-embed-primary" href={mapsUrl} target="_blank" rel="noopener noreferrer">
          <Navigation size={16} aria-hidden="true" /> Get Directions
        </a>
        <a className="map-embed-chip" href={appleUrl} target="_blank" rel="noopener noreferrer"><MapPin aria-hidden="true" /> Apple Maps</a>
        <a className="map-embed-chip" href={wazeUrl}  target="_blank" rel="noopener noreferrer"><Navigation aria-hidden="true" /> Waze</a>
        {/* New round #65: coords hidden, surfaced via Copy button */}
        <button
          type="button"
          onClick={onCopyCoords}
          className="map-embed-copy"
          aria-label={copied ? "Coordinates copied" : "Copy GPS coordinates to clipboard"}
        >
          {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          {copied ? "Copied" : "Copy GPS coords"}
        </button>
        <a
          className="map-embed-chip"
          href={`https://earth.google.com/web/@${business.geo.lat},${business.geo.lng}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Preview the property in Google Earth"
        >
          <Eye aria-hidden="true" /> Preview the property
        </a>
      </div>
    </section>
  );
}
